import type { TypedDocumentNode } from '@apollo/client';
import { Button, Stack, Typography } from '@mui/material';
import Banner from 'src/components/atoms/Banner';
import { preloadRouteQuery } from 'src/routing/routePreload';
import type { AppRouterContext } from 'src/routing/routerAccess';
import { useRouteLoaderQuery } from 'src/routing/useRouteLoaderQuery';
import type { AiForwardingConfirmationQuery, AiForwardingConfirmationQueryVariables } from 'types/graphql';
import { useClearAiForwardingConfirmation } from './hooks/useClearAiForwardingConfirmation';

const AI_FORWARDING_CONFIRMATION_QUERY: TypedDocumentNode<
  AiForwardingConfirmationQuery,
  AiForwardingConfirmationQueryVariables
> = gql`
  query AiForwardingConfirmationQuery {
    aiForwardingConfirmation {
      verifyEmailLink
      denyEmailLink
      issuedAt
      forwardingFromEmail
    }
    orgSettings {
      aiOrderProcessingEmail
    }
  }
`;

export function preloadAiForwardingConfirmation(context: AppRouterContext) {
  return preloadRouteQuery(context, AI_FORWARDING_CONFIRMATION_QUERY, {}, { fetchPolicy: 'cache-and-network' });
}

type Props = {
  /** Called after the user clicks approve or deny */
  onAction?: () => void;
};

/**
 * Banner component that displays when there's a pending email forwarding confirmation.
 * This is designed to be easily movable to any page (settings, integrations, dashboard, etc.)
 */
export default function AiForwardingConfirmationBanner({ onAction }: Props) {
  const { data, loading, refetch, reader } = useRouteLoaderQuery(AI_FORWARDING_CONFIRMATION_QUERY, {});
  const { clear: clearConfirmation } = useClearAiForwardingConfirmation({
    onCompleted: () => {
      refetch();
      onAction?.();
    },
  });

  if (loading || !data?.aiForwardingConfirmation) {
    return reader;
  }

  const { verifyEmailLink, denyEmailLink, forwardingFromEmail } = data.aiForwardingConfirmation;
  const aiOrderProcessingEmail = data.orgSettings?.aiOrderProcessingEmail;

  const handleApprove = () => {
    // Open the verify link in a new tab
    window.open(verifyEmailLink, '_blank');
    // Clear the confirmation after user takes action
    clearConfirmation();
  };

  const handleDeny = () => {
    // Open the deny link in a new tab
    window.open(denyEmailLink, '_blank');
    // Clear the confirmation after user takes action
    clearConfirmation();
  };

  return (
    <>
      {reader}
      <Banner
        variant="info"
        title="Email Forwarding Confirmation Required"
        subtitle={
          <Stack spacing={2}>
            <Stack spacing={0.5}>
              <Typography variant="body2" component="span">
                <strong>{forwardingFromEmail}</strong> has requested permission to forward emails to{' '}
                <strong>{aiOrderProcessingEmail}</strong>.
              </Typography>
              <Typography variant="body2" component="span">
                This will enable emails sent to <strong>{forwardingFromEmail}</strong> to be processed by the Agriful AI
                Assistant. Only approve this if you trust this email address.
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Button variant="outlined" size="small" onClick={handleApprove}>
                Approve
              </Button>
              <Button variant="outlined" color="error" size="small" onClick={handleDeny}>
                Deny
              </Button>
            </Stack>
            <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
              Follow the instructions on the page that opens.
            </Typography>
          </Stack>
        }
        className="mb-2"
      />
    </>
  );
}
